import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { apiUrl } from '../api';

const TOKEN_KEY = 'auth_token';

// Dev bypass: check for ?dev=true in URL (only works in development)
const isDevBypass =
  import.meta.env.DEV && new URLSearchParams(window.location.search).get('dev') === 'true';

const DEV_USER: User = {
  id: 0,
  email: 'dev@localhost',
  name: 'Dev User',
  picture: null,
  username: 'dev_user',
  isAdmin: true,
};

export type User = {
  id: number;
  email: string;
  name: string | null;
  picture: string | null;
  username: string | null;
  isAdmin?: boolean;
};

type AuthContextType = {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  hasUsername: boolean;
  login: (credential: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  getAuthHeaders: () => HeadersInit;
  updateUser: (user: User) => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

type AuthProviderProps = {
  children: ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = useCallback((): HeadersInit => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
    return {};
  }, []);

  const checkAuth = useCallback(async () => {
    // Dev bypass: skip auth check and use mock user
    if (isDevBypass) {
      setUser(DEV_USER);
      setLoading(false);
      return;
    }

    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(apiUrl('/auth/me?platform=web'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      } else {
        // Token invalid - remove it
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (credential: string) => {
    const response = await fetch(apiUrl('/auth/verify'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ credential, platform: 'web' }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Login failed');
    }

    const data = await response.json();
    // Store the JWT token
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
  }, []);

  const logout = useCallback(async () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  const updateUser = useCallback((updatedUser: User) => {
    setUser(updatedUser);
  }, []);

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const value: AuthContextType = {
    user,
    loading,
    isAuthenticated: !!user,
    hasUsername: !!user?.username,
    login,
    logout,
    checkAuth,
    getAuthHeaders,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
